import { useState, type Dispatch, type KeyboardEvent, type SetStateAction } from 'react';
import ApiRequest from 'Shared/api_2_0';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { resolveFaLegacyIcon } from 'Shared/faLegacyIcon';
import { SectionHeading } from './section_heading';
import { isMobileApp } from 'Shared/utils/mobile-app-helpers';

type NotificationType = {
   read: boolean;
   signature: string;
   metadata: {
      icon: string;
      url: string;
      message: string;
      date: string;
      relative_time: string;
      human_readable_time: string;
      context: string;
   };
};

type NotificationData = {
   notif_count: number;
   recent_notifications: NotificationType[];
   unread_notifications: NotificationType[];
   own_profile: boolean;
};

export function Notifications({
   data,
   setUnreadNotifCount,
}: {
   data: NotificationData;
   setUnreadNotifCount: Dispatch<SetStateAction<number>>;
}) {
   // The notification state changes only when we need to make an API call
   // to refresh notification metadata (i.e. marking a notification as read)
   const [recentNotifications, setRecentNotifications] = useState(data.recent_notifications);
   const [unreadNotifications, setUnreadNotifications] = useState(data.unread_notifications);

   // The filter state changes when we toggle between All/Unread notifs.
   // Client already has all necessary data so no need for an API call,
   // we just use conditional rendering.
   const [filter, setFilter] = useState('all');

   const visibleNotifications: NotificationType[] =
      filter === 'all' ? recentNotifications : unreadNotifications;

   return (
      <div className="flex flex-col">
         <SectionHeading>{_js('Notifications')}</SectionHeading>
         <NotificationOptions
            recentNotifications={recentNotifications}
            setRecentNotifications={setRecentNotifications}
            unreadNotifications={unreadNotifications}
            setUnreadNotifications={setUnreadNotifications}
            setUnreadNotifCount={setUnreadNotifCount}
            filter={filter}
            setFilter={setFilter}
            ownProfile={data.own_profile}
         />
         {visibleNotifications.length > 0 ? (
            <NotificationList
               notifs={visibleNotifications}
               recentNotifications={recentNotifications}
               unreadNotifications={unreadNotifications}
               ownProfile={data.own_profile}
               setRecentNotifications={setRecentNotifications}
               setUnreadNotifications={setUnreadNotifications}
               setUnreadNotifCount={setUnreadNotifCount}
            />
         ) : (
            <NoNotifications />
         )}
      </div>
   );
}

const NotificationOptions = ({
   recentNotifications,
   setRecentNotifications,
   unreadNotifications,
   setUnreadNotifications,
   filter,
   setFilter,
   setUnreadNotifCount,
   ownProfile,
}: {
   recentNotifications: NotificationType[];
   setRecentNotifications: (_notifications: NotificationType[]) => void;
   unreadNotifications: NotificationType[];
   setUnreadNotifications: (_notifications: NotificationType[]) => void;
   filter: string;
   setFilter: (_filter: string) => void;
   setUnreadNotifCount: Dispatch<SetStateAction<number>>;
   ownProfile: boolean;
}) => {
   const hasUnreadNotifications = unreadNotifications.length > 0;
   const showMarkAllReadButton = hasUnreadNotifications && ownProfile;
   return (
      <div className="flex justify-between mt-6 mb-8">
         <FilterSelect filter={filter} setFilter={setFilter} />
         {showMarkAllReadButton ? (
            <MarkAllReadButton
               recentNotifications={recentNotifications}
               setRecentNotifications={setRecentNotifications}
               setUnreadNotifications={setUnreadNotifications}
               setUnreadNotifCount={setUnreadNotifCount}
            />
         ) : null}
      </div>
   );
};

const MarkAllReadButton = ({
   recentNotifications,
   setRecentNotifications,
   setUnreadNotifications,
   setUnreadNotifCount,
}: {
   recentNotifications: NotificationType[];
   setRecentNotifications: (_notifications: NotificationType[]) => void;
   setUnreadNotifications: (_notifications: NotificationType[]) => void;
   setUnreadNotifCount: Dispatch<SetStateAction<number>>;
}) => {
   function markAllRead() {
      new ApiRequest('notifications/all', {
         method: 'DELETE',
      }).send();
      setRecentNotifications(
         recentNotifications.map((notif: NotificationType) => ({ ...notif, read: true }))
      );
      setUnreadNotifications([]);
      setUnreadNotifCount(0);
   }

   return (
      <button
         className="h-[28px] px-1.5 rounded-lg font-semibold hover:bg-gray-200 transition-colors duration-150"
         onClick={markAllRead}
      >
         {_js('Mark all as read')}
      </button>
   );
};

const FilterSelect = ({
   filter,
   setFilter,
}: {
   filter: string;
   setFilter: (_filter: string) => void;
}) => (
   <div className="flex items-center gap-[10px]">
      <button
         onClick={() => setFilter('all')}
         className={`h-[28px] px-1.5 rounded-lg ${filter === 'all' ? 'font-semibold bg-gray-200' : 'font-normal bg-gray-100'}`}
      >
         {_js('All')}
      </button>
      <button
         onClick={() => setFilter('unread')}
         className={`h-[28px] px-1.5 rounded-lg ${filter === 'unread' ? 'font-semibold bg-gray-200' : 'font-normal bg-gray-100'}`}
      >
         {_js('Unread')}
      </button>
   </div>
);

const NoNotifications = () => (
   <div className="flex flex-col items-center">
      <FaIcon name="face-smile" style="regular" className="size-[50px]" />
      <p>{_js("You're on top of things! No new notifications.")}</p>
   </div>
);

const NotificationList = ({
   notifs,
   recentNotifications,
   unreadNotifications,
   ownProfile,
   setRecentNotifications,
   setUnreadNotifications,
   setUnreadNotifCount,
}: {
   notifs: NotificationType[];
   recentNotifications: NotificationType[];
   unreadNotifications: NotificationType[];
   ownProfile: boolean;
   setRecentNotifications: (_notifs: NotificationType[]) => void;
   setUnreadNotifications: (_notifs: NotificationType[]) => void;
   setUnreadNotifCount: Dispatch<SetStateAction<number>>;
}) => {
   const markAsRead = (readNotif: NotificationType) => {
      const readSignature = readNotif.signature;
      new ApiRequest(`notifications/?ids=${readSignature}`).send();

      // Update recent notifications
      const updatedRecentNotifications = recentNotifications.map(
         (notification: NotificationType) =>
            notification.signature === readSignature
               ? { ...notification, read: true }
               : notification
      );
      setRecentNotifications(updatedRecentNotifications);

      // Update unread notifications
      const updatedUnreadNotifications = unreadNotifications.filter(
         (notification: NotificationType) => notification.signature !== readSignature
      );
      setUnreadNotifications(updatedUnreadNotifications);

      // The badge counts every unread notification, not just the 50 this list
      // holds, so it can't be recomputed from unreadNotifications.length.
      setUnreadNotifCount((count: number) => Math.max(0, count - 1));
   };

   return (
      <div className="flex flex-col gap-4">
         {notifs.map((notification: NotificationType, index: number) => (
            <NotificationListItem
               key={index}
               notification={notification}
               ownProfile={ownProfile}
               markAsRead={markAsRead}
            />
         ))}
      </div>
   );
};

const NotificationListItem = ({
   notification,
   ownProfile,
   markAsRead,
}: {
   notification: NotificationType;
   ownProfile: boolean;
   markAsRead: (_notif: NotificationType) => void;
}) => {
   const unreadIcon = !notification.read;
   const target = notification.metadata.url;
   const isCurrentPage = window.location.pathname === target;
   const isDeepLink = target.startsWith('ifixitmobile://');
   const url = isDeepLink && !isMobileApp() ? '/go/app' : target;
   const icon = resolveFaLegacyIcon(notification.metadata.icon);

   // Nothing to click on a read row or on someone else's profile.
   const canMarkRead = !notification.read && ownProfile;

   const markThisRead = () => {
      if (canMarkRead) {
         markAsRead(notification);
      }
   };

   const markThisReadOnKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
         event.preventDefault();
         markThisRead();
      }
   };

   const content = (
      <>
         {notification.metadata.message}.{' '}
         <span className="text-gray-500 whitespace-nowrap">
            {notification.metadata.human_readable_time}
         </span>
         {notification.metadata.context ? (
            <>
               <br />
               <span className="text-gray-500 whitespace-nowrap">
                  {notification.metadata.context}
               </span>
            </>
         ) : null}
      </>
   );

   return (
      <div className="flex justify-between items-center relative">
         {unreadIcon ? (
            <div className="absolute top-[calc(50%-6px)] left-[-10px] md:left-[-16px] size-1.5 bg-brand-500 rounded-full translate-y-1/2" />
         ) : null}
         <div className="flex items-center gap-3">
            <div className="flex items-center min-w-[48px] size-[48px] rounded-lg bg-gray-100 justify-center text-[20px]">
               {icon ? (
                  <FaIcon
                     name={icon.name}
                     style={icon.style}
                     className="size-[20px] text-gray-400"
                  />
               ) : null}
            </div>
            {/* A notification whose target document is gone carries no url. */}
            {isCurrentPage || !url ? (
               <span
                  className={`text-black ${canMarkRead ? 'cursor-pointer' : ''}`}
                  style={{ fontWeight: notification.read ? 'normal' : 'semibold' }}
                  role={canMarkRead ? 'button' : undefined}
                  tabIndex={canMarkRead ? 0 : undefined}
                  onClick={markThisRead}
                  onKeyDown={canMarkRead ? markThisReadOnKey : undefined}
               >
                  {content}
               </span>
            ) : (
               <a
                  className="text-black"
                  style={{ fontWeight: notification.read ? 'normal' : 'semibold' }}
                  onClick={() => {
                     markThisRead();
                     window.open(url, '_blank');
                  }}
               >
                  {content}
               </a>
            )}
         </div>
      </div>
   );
};
